import React, { useEffect, useMemo, useState } from "react";
import { Bell, AlertTriangle, Info, AlertOctagon, Check, CheckCheck } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const STORAGE_KEY = "vcrm_dismissed_notifs_v1";

const loadDismissed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

const saveDismissed = (set) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(loadDismissed);

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setItems(res.data.items || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Prune dismissed entries that no longer exist (auto-cleanup)
  useEffect(() => {
    if (items.length === 0) return;
    const liveIds = new Set(items.map((i) => i.id));
    const cleaned = new Set([...dismissed].filter((id) => liveIds.has(id)));
    if (cleaned.size !== dismissed.size) {
      setDismissed(cleaned);
      saveDismissed(cleaned);
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(
    () => items.filter((i) => !dismissed.has(i.id)),
    [items, dismissed]
  );

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  const dismissAll = () => {
    const next = new Set(items.map((i) => i.id));
    setDismissed(next);
    saveDismissed(next);
  };

  const iconFor = (sev) => {
    if (sev === "danger") return <AlertOctagon className="h-4 w-4 text-red-600" />;
    if (sev === "warning") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
    return <Info className="h-4 w-4 text-blue-600" />;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell-btn"
        >
          <Bell className="h-5 w-5" />
          {visible.length > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center px-1"
              data-testid="notification-count"
            >
              {visible.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0"
        data-testid="notification-panel"
      >
        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Notifications</div>
            <div className="text-sm text-gray-500">
              {visible.length} unread · {items.length - visible.length} dismissed
            </div>
          </div>
          {visible.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={dismissAll}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {visible.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              All caught up — no reminders.
            </div>
          ) : (
            visible.map((n) => (
              <button
                type="button"
                key={n.id}
                onClick={() => dismiss(n.id)}
                className="w-full text-left p-3 border-b border-gray-100 last:border-b-0 flex gap-3 hover:bg-gray-50 transition-colors group"
                data-testid="notification-item"
              >
                <div className="mt-0.5">{iconFor(n.severity)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{n.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{n.message}</div>
                  <div className="text-[10px] text-gray-400 mt-1 font-mono">
                    {n.date}
                  </div>
                </div>
                <Check className="h-4 w-4 text-gray-300 group-hover:text-emerald-600 mt-0.5 shrink-0" />
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
